/**
 * Logística → Pagos Adelantados (`/logistica/pagos-adelantados`).
 *
 * Puerto de `Prepaid::ventas` + `views/prepaid/ventas.php` + `src/prepaid/ventas.js`
 * (que se alimentaba de `API/Prepaid::sales`): el histórico de pagos adelantados
 * con sus cuatro contadores (transacciones, total despachado, monto total y
 * clientes) y la anulación del pago.
 *
 * La **anulación** se ofrece en los pagos completados cuyo turno está verificado,
 * igual que el panel anterior: libera las transacciones del surtidor, devuelve la
 * cantidad comprometida y marca el pago como cancelado. El **ticket** ya lo genera
 * el propio panel (PDF de la API, `Prepaid::print_ticket` en el anterior) y el
 * **detalle** (`Prepaid::detail`) es otra pantalla que queda pendiente con su
 * candado.
 */

import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Eye,
  HandCoins,
  Printer,
  Search,
  ShieldAlert,
  User,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { useAnularPagoAdelantado, usePagosAdelantados } from '../pagos-adelantados.api';
import type { PagoAdelantado, PagosAdelantadosFiltros } from '../pagos-adelantados.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Color del distintivo según el estado del pago. */
function varianteEstado(estado: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (estado === 'completed') return 'success';
  if (estado === 'pending') return 'warning';
  if (estado === 'cancelled') return 'danger';

  return 'neutral';
}

/** Cuadro de total del histórico. */
function Contador({
  titulo,
  valor,
  nota,
  icono: Icono,
}: {
  titulo: string;
  valor: string;
  nota?: string;
  icono: typeof Wallet;
}) {
  return (
    <div className="flex items-center gap-3 rounded-card border border-ink-100 bg-white p-3 shadow-soft">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600">
        <Icono className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
          {titulo}
        </span>
        <span className="block text-sm font-semibold text-ink-900">{valor}</span>
        {nota ? <span className="block text-[11px] text-ink-400">{nota}</span> : null}
      </div>
    </div>
  );
}

export function PagosAdelantadosPage() {
  useDocumentTitle('Logística · Pagos Adelantados');

  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<PagosAdelantadosFiltros>({
    perPage: POR_PAGINA,
    page: 1,
  });
  const [aAnular, setAAnular] = useState<PagoAdelantado | null>(null);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const pagos = usePagosAdelantados(filtros);
  const anular = useAnularPagoAdelantado();

  const sinPermiso = pagos.error instanceof ApiError && pagos.error.status === 403;
  const lista = pagos.data?.pagos ?? [];
  const total = pagos.data?.total ?? 0;
  const pagina = pagos.data?.page ?? 1;
  const paginas = pagos.data?.paginas ?? 1;
  const resumen = pagos.data?.resumen;
  // Pagos del filtro actual cuyo detalle de venta no está en la base.
  const sinDetalle = resumen?.sin_detalle ?? 0;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica el buscador y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      search: busqueda.trim() || undefined,
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Abre el ticket del pago (lo genera la propia API). */
  async function imprimirTicket(pago: PagoAdelantado) {
    setErrorImpresion(null);
    setImprimiendo(pago.id);

    try {
      await abrirPdfAutenticado(`/api/logistica/pagos-adelantados/${pago.id}/ticket`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el ticket.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <HandCoins className="size-6 text-brand-500" aria-hidden="true" />
          Pagos Adelantados
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Combustible pagado por adelantado y despachado con vale, con el conductor, la placa y la
          anulación del pago.
        </p>
      </div>

      {pagos.data ? <Badge variant="outline">{formatNumber(total)} registros</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            {mensajeDe(pagos.error, 'Esta pantalla es de los grupos admin, credits y finance.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        La <strong>anulación</strong> ya está disponible y el <strong>ticket</strong> lo genera el
        propio panel (falta el envío a la impresora térmica). El <strong>detalle del pago</strong> es
        otra pantalla del panel anterior, así que se portará con ese circuito.
      </Alert>

      <Alert variant="info" title="De dónde salen el cliente y los importes">
        Cada pago consume un <strong>detalle de la venta</strong> que pagó el combustible por
        adelantado: de ahí salen el cliente, el <strong>comprobante</strong> (su factura o boleta),
        el producto, el precio y el total (<em>cantidad × precio</em>). Cuando ese detalle no está
        enlazado en la base esas columnas quedan vacías, como en el panel anterior, y el monto no se
        puede calcular.{' '}
        {sinDetalle > 0 ? (
          <>
            En este listado, <strong>{formatNumber(sinDetalle)}</strong> de{' '}
            <strong>{formatNumber(total)}</strong> pagos apuntan a un detalle que no está en la base
            (entre paréntesis, el número que tienen guardado).
          </>
        ) : null}
      </Alert>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Contador
          titulo="Total transacciones"
          valor={formatNumber(resumen?.transacciones ?? 0)}
          icono={HandCoins}
        />
        <Contador
          titulo="Total despachado"
          valor={formatNumber(resumen?.cantidad ?? 0)}
          icono={Wallet}
        />
        <Contador
          titulo="Monto total"
          valor={formatCurrency(resumen?.monto ?? 0)}
          nota="Sólo de los pagos con el detalle de venta enlazado"
          icono={Wallet}
        />
        <Contador
          titulo="Clientes"
          valor={formatNumber(resumen?.clientes ?? 0)}
          icono={User}
        />
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="pa-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="pa-buscar"
                className={cn(CONTROL, 'w-72')}
                placeholder="Cliente, producto, conductor, placa…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') aplicar();
                }}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Search className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Buscar
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBusqueda('');
                setFiltros({ perPage: POR_PAGINA, page: 1 });
              }}
            >
              Limpiar
            </Button>
          </div>

          {anular.error ? (
            <Alert variant="error" title="No se pudo anular el pago">
              {mensajeDe(anular.error, 'La API no respondió.')}
            </Alert>
          ) : null}

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar el ticket">
              {errorImpresion}
            </Alert>
          ) : null}

          {pagos.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : pagos.error ? (
            <Alert variant="error" title="No se pudieron leer los pagos adelantados">
              {mensajeDe(pagos.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay pagos adelantados con esa búsqueda.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Estado</th>
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2">Comprobante</th>
                    <th className="py-2">Producto</th>
                    <th className="py-2 text-right">Cantidad</th>
                    <th className="py-2 text-right">Precio</th>
                    <th className="py-2 text-right">Total</th>
                    <th className="py-2">Conductor</th>
                    <th className="py-2">Documento</th>
                    <th className="py-2">Placa</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((pago) => (
                    <tr key={pago.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3">
                        <Badge variant={varianteEstado(pago.estado)}>{pago.estado_legible}</Badge>
                        {pago.turno_id ? (
                          <span className="mt-1 block text-[11px] text-ink-400">
                            Turno #{pago.turno_id}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 text-ink-700">{pago.fecha ?? '—'}</td>
                      <td className="py-3">
                        {pago.cliente ? (
                          <>
                            <span className="block font-semibold text-ink-900">{pago.cliente}</span>
                            <span className="block text-[11px] text-ink-400">
                              {pago.documento_cliente}
                            </span>
                          </>
                        ) : (
                          <span
                            className="text-xs text-ink-300"
                            title="El pago consume un detalle de venta que no está en esta base, así que no se puede saber a qué cliente ni a qué comprobante corresponde (el panel anterior tampoco lo mostraba)."
                          >
                            Sin detalle enlazado
                            {pago.detalle_id ? (
                              <span className="block text-[11px] text-ink-300">
                                Detalle #{pago.detalle_id}
                              </span>
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        {pago.comprobante ? (
                          <>
                            <span className="block text-ink-700">
                              {pago.tipo_comprobante} {pago.comprobante}
                            </span>
                            <span className="block text-[11px] text-ink-400">
                              {pago.estado_sunat || 'sin estado SUNAT'}
                              {pago.fecha_venta ? ` · ${pago.fecha_venta}` : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-3 text-ink-700">
                        {pago.producto || <span className="text-xs text-ink-300">—</span>}
                        {pago.producto_codigo ? (
                          <span className="block text-[11px] text-ink-400">
                            {pago.producto_codigo}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 text-right text-ink-700">
                        {formatNumber(pago.cantidad)}
                      </td>
                      <td className="py-3 text-right text-ink-700">
                        {pago.precio === null ? (
                          <span className="text-xs text-ink-300">—</span>
                        ) : (
                          formatCurrency(pago.precio)
                        )}
                      </td>
                      <td className="py-3 text-right font-semibold text-ink-900">
                        {pago.total === null ? (
                          <span className="text-xs font-normal text-ink-300">—</span>
                        ) : (
                          formatCurrency(pago.total)
                        )}
                      </td>
                      <td className="py-3 text-ink-700">
                        {pago.conductor || <span className="text-xs text-ink-300">—</span>}
                        {pago.kilometraje !== null ? (
                          <span className="block text-[11px] text-ink-400">
                            Km {formatNumber(pago.kilometraje)}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 text-ink-700">
                        {pago.documento_conductor || (
                          <span className="text-xs text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-3">
                        {pago.placa ? (
                          <Badge variant={pago.estado === 'completed' ? 'success' : 'neutral'}>
                            {pago.placa}
                          </Badge>
                        ) : (
                          <span className="text-xs text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Imprimir el ticket del pago"
                            aria-label={`Imprimir el ticket del pago ${pago.id}`}
                            loading={imprimiendo === pago.id}
                            onClick={() => void imprimirTicket(pago)}
                          >
                            <Printer className="size-4 text-ink-600" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled
                            title="Ver el pago adelantado · es otra pantalla del panel anterior"
                          >
                            <Eye className="size-4 text-ink-300" aria-hidden="true" />
                          </Button>
                          {pago.puede_anular ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Anular el pago adelantado"
                              aria-label={`Anular el pago adelantado ${pago.id}`}
                              onClick={() => {
                                anular.reset();
                                setAAnular(pago);
                              }}
                            >
                              <Ban className="size-4 text-red-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled
                              title={
                                pago.puede_cancelar
                                  ? 'Anular · el turno todavía no está verificado'
                                  : 'Anular · el pago ya está cancelado'
                              }
                            >
                              <Ban className="size-4 text-ink-300" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} registros · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {aAnular ? (
        <ConfirmDialog
          open
          onClose={() => setAAnular(null)}
          onConfirm={() =>
            anular.mutate(aAnular.id, { onSuccess: () => setAAnular(null) })
          }
          title="¿Anular el pago adelantado?"
          description="Se liberarán las transacciones del surtidor para que se puedan facturar otra vez y se devolverá la cantidad comprometida al detalle de venta. El pago quedará cancelado."
          confirmLabel="Sí, anular"
          loading={anular.isPending}
        />
      ) : null}
    </div>
  );
}
